import { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { GlobalStates } from "../GlobalStates";
import PrimaryButton from "../Components/Button/PrimaryButton";
import Link from "@mui/icons-material/Link";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Snackbar, CircularProgress } from "@mui/material";
import { t } from "i18next";
import { useSupabaseData } from "../Hooks";
import { supabase } from "../services/supabaseClient";
 
const FormTable = () => {
    const { user, languageG } = useContext(GlobalStates);
    
    const currentYear = new Date().getFullYear();

    const [open, setOpen] = useState(false);
    
    const [uzpildytosFormos, setUzpildytosFormos] = useState([]);
    const [isCreating, setIsCreating] = useState(false);
    const [creatingFormId, setCreatingFormId] = useState(null);
    const [formsData, setFormsData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [initialLoadTriggered, setInitialLoadTriggered] = useState(false); // Naujas kintamasis
    
    // Hooks for form templates - disabled auto sync
    const { 
        data: tipai, 
        getAll: getAllTipai,
        isLoading: tipaiLoading 
    } = useSupabaseData({ name: 'formos_tipai', skipAutoSync: true }, []);

    // Direct load without using Hook cache
    const loadUserForms = useCallback(async () => {
        const companyName = user?.user_metadata?.imones_pavadinimas;
        if (!companyName) return [];
        
        try {
            const { data, error } = await supabase
                .from('formos')
                .select('*')
                .eq('imones_pavadinimas', companyName);
                
            if (error) throw error;
            
            // Format data for frontend
            const formattedData = (data || []).map(form => ({
                id: form.id,
                attributes: {
                    formosPavadinimas: form.formos_pavadinimas,
                    imonesPavadinimas: form.imones_pavadinimas,
                    kasPildo: form.kas_pildo,
                    type: form.type,
                    validTo: form.valid_to,
                    formObject: form.form_object,
                    createdAt: form.created_at,
                    updatedAt: form.updated_at
                }
            }));
            
            setFormsData(formattedData);
            return formattedData;
        } catch (err) {
            console.error("Error loading user forms:", err);
            return [];
        }
    }, [user?.user_metadata?.imones_pavadinimas]);

    // Get filled forms information
    const getUzpildytosFormos = useCallback(async () => {
        const companyName = user?.user_metadata?.imones_pavadinimas;
        if (!companyName) return [];
        
        try {
            const { data, error } = await supabase
                .from('uzpildytos_imones')
                .select('*')
                .eq('imones_pavadinimas', companyName);
                
            if (error) throw error;
            
            setUzpildytosFormos(data || []);
            return data || [];
        } catch (err) {
            console.error("Error loading filled forms:", err);
            return [];
        }
    }, [user?.user_metadata?.imones_pavadinimas]);

    // Direct load for templates
    const loadTemplates = useCallback(async () => {
        try {
            const { data, error } = await supabase
                .from('formos_tipai')
                .select('*');
                
            if (error) throw error;
            
            // Format data for frontend (same format as hook)
            const formattedData = (data || []).map(form => ({
                id: form.id,
                attributes: {
                    formosPavadinimas: form.formos_pavadinimas,
                    kasPildo: form.kas_pildo,
                    type: form.type,
                    validTo: form.valid_to,
                    formObject: form.form_object
                }
            }));
            
            return formattedData;
        } catch (err) {
            console.error("Error loading templates:", err);
            return [];
        }
    }, []);

    // Load all required data directly, bypassing hooks if needed
    const loadAllData = useCallback(async () => {
        // Patikrinti ar turime reikalingus vartotojo duomenis
        if (!user || !user.id || !user.user_metadata?.imones_pavadinimas) {
            return;
        }
        setIsLoading(true);
        
        try {
            // First try using the hook
            if (getAllTipai && typeof getAllTipai === 'function') {
                await getAllTipai();
            } else {
                // Fallback to direct loading
                await loadTemplates();
            }
            
            // Load user forms and filled forms directly only if we have user data
            if (user && user.user_metadata?.imones_pavadinimas) {
                await Promise.all([
                    loadUserForms(),
                    getUzpildytosFormos()
                ]);
            }
        } catch (error) {
            console.error("Error caught in loadAllData:", error); // Log error
        } finally {
            setIsLoading(false);
        }
    }, [user, getAllTipai, loadTemplates, loadUserForms, getUzpildytosFormos]);

    // Load data when user context is ready
    useEffect(() => {
        // Patikrinti ar vartotojo kontekstas pilnai užkrautas ir ar tai pirmas krovimas
        if (user && user.id && user.user_metadata?.imones_pavadinimas && !initialLoadTriggered) {
            loadAllData();
            setInitialLoadTriggered(true); // Pažymime, kad pradinis krovimas įvyko
        }
    }, [user, user?.id, user?.user_metadata?.imones_pavadinimas, initialLoadTriggered, loadAllData]); // Pridedame initialLoadTriggered ir user prie priklausomybių

    // Reload templates when language changes
    useEffect(() => {
        if (user && getAllTipai) {
            getAllTipai();
        }
    }, [languageG, user, getAllTipai]);
    
    // Automatiškai atnaujinti duomenis kas 30 sekundžių, jei turime vartotoją
    useEffect(() => {
        let autoRefreshTimer;
        
        if (!isLoading && user?.user_metadata?.imones_pavadinimas) {
            // Nustatome laikmatį automatiškam atnaujinimui kas 30 sekundžių
            autoRefreshTimer = setTimeout(() => {
                loadAllData();
            }, 30000); // 30 sekundžių vietoj 3 sekundžių
        }
        
        return () => {
            // Išvalome laikmatį, kai komponentas atnaujinamas arba išmontuojamas
            if (autoRefreshTimer) {
                clearTimeout(autoRefreshTimer);
            }
        };
    }, [isLoading, user?.user_metadata?.imones_pavadinimas, loadAllData]);
    
    // Check if a form is created for the current user
    const isFormaSukurta = useCallback((pavadinimas, kasPildo) => {
        return formsData.some(form => 
            form.attributes.formosPavadinimas === pavadinimas &&
            form.attributes.kasPildo === kasPildo
        );
    }, [formsData]);

    // Check if form is already filled out (company forms only once per year)
    const isFormaUzpildyta = useCallback((pavadinimas, kasPildo) => {
        if (kasPildo !== 'Įmonės forma') return false;
        
        return uzpildytosFormos.some(item => 
            item.paskutiniai_metai === currentYear && 
            (item.uzpildytos_formos || []).includes(pavadinimas) &&
            (item.kas_pilde || []).some(pildejas => pildejas.includes('Įmonės'))
        );
    }, [uzpildytosFormos, currentYear]);

    // Get form ID by name and type
    const getFormId = useCallback((pavadinimas, kasPildo) => {
        const form = formsData.find(form => 
            form.attributes.formosPavadinimas === pavadinimas &&
            form.attributes.kasPildo === kasPildo
        );
        
        return form ? form.id : null;
    }, [formsData]);
    
    // Open the form for editing/viewing
    const openForm = useCallback((formId) => {
        if (formId) {
            window.open(window.location.origin + '/Formos/' + formId);
        } else {
            console.error("Cannot open form: form ID is not available");
        }
    }, []);
    
    // Create a new form based on a template
    const createForm = useCallback(async (templateAttr) => {
        if (isCreating) return;
        
        const templateId = templateAttr.id;
        setIsCreating(true);
        setCreatingFormId(templateId);
        
        try {
            // Prepare new form data
            const newFormData = { 
                type: templateAttr.type,
                valid_to: templateAttr.validTo,
                formos_pavadinimas: templateAttr.formosPavadinimas,
                imones_pavadinimas: user.user_metadata.imones_pavadinimas,
                form_object: templateAttr.formObject,
                kas_pildo: templateAttr.kasPildo,
                formos_tipai_id: templateAttr.id,
                user_id: user.id,
                slaptazodis: ''
            };

            // Insert new form into database
            const { data: createdForm, error } = await supabase
                .from('formos')
                .insert([newFormData])
                .select()
                .single();

            if (error) throw error;
            if (!createdForm) throw new Error("Form creation failed");

            // Format for frontend
            const formattedForm = {
                id: createdForm.id,
                attributes: {
                    formosPavadinimas: createdForm.formos_pavadinimas,
                    imonesPavadinimas: createdForm.imones_pavadinimas,
                    kasPildo: createdForm.kas_pildo,
                    type: createdForm.type,
                    validTo: createdForm.valid_to,
                    formObject: createdForm.form_object,
                    createdAt: createdForm.created_at,
                    updatedAt: createdForm.updated_at
                }
            };

            // Update local state directly instead of relying on cache
            setFormsData(currentForms => [...currentForms, formattedForm]);

            // Show success message
            alert(t('forma_sukurta_sekimingai'));
        } catch (error) {
            console.error("Error creating form:", error);
            alert("Klaida kuriant formą. Prašome bandyti dar kartą.");
        } finally {
            setIsCreating(false);
            setCreatingFormId(null);
        }
    }, [isCreating, user?.user_metadata?.imones_pavadinimas, user?.id]);
    
    // Copy form link to clipboard
    const copyFormLink = useCallback((formId) => {
        if (formId) {
            setOpen(true);
            navigator.clipboard.writeText(window.location.origin + '/Formos/' + formId);
            
            // Auto-close notification after 2 seconds
            setTimeout(() => {
                setOpen(false);
            }, 2000);
        } else {
            console.error("Cannot copy link: form ID is not available");
        }
    }, []);
    
    // Sort templates by type, validity date, and name
    const sortTemplates = useMemo(() => (templates) => {
        if (!templates || templates.length === 0) return [];
        
        return [...templates].sort((a, b) => {
            // Sort by type
            if (a.attributes.type !== b.attributes.type) {
                return a.attributes.type.localeCompare(b.attributes.type);
            }
            // Then by validity date (newest first)
            else if (a.attributes.validTo !== b.attributes.validTo) {
                return new Date(b.attributes.validTo) - new Date(a.attributes.validTo);
            }
            // Finally by name
            else {
                return a.attributes.formosPavadinimas.localeCompare(b.attributes.formosPavadinimas);
            }
        });
    }, []);
    
    // Show loading state
    if (isLoading || tipaiLoading) {
        return (
            <div className="Formos standartPadding">
                <div className="row name_action">
                    <p className="accorcionName">{t('atlikti_vertinima_label')}</p>
                    <button 
                        className="refresh-btn" 
                        onClick={() => loadAllData()}
                        style={{ 
                            marginLeft: 'auto', 
                            background: 'none', 
                            border: 'none',
                            cursor: 'pointer',
                            color: '#0056b3',
                            fontSize: '14px'
                        }}
                        disabled={isLoading || tipaiLoading}
                    >
                        ↻ Atnaujinti
                    </button>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                    <CircularProgress />
                    <span style={{ marginLeft: '1rem' }}>Kraunama...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="Formos standartPadding">
            <div className="row name_action">
                <p className="accorcionName">{t('atlikti_vertinima_label')}</p>
                <button 
                    className="refresh-btn" 
                    onClick={() => loadAllData()}
                    style={{ 
                        marginLeft: 'auto', 
                        background: 'none', 
                        border: 'none',
                        cursor: 'pointer',
                        color: '#0056b3',
                        fontSize: '14px'
                    }}
                >
                    ↻ Atnaujinti
                </button>
            </div>

            {/* Global notification for link copying */}
            <Snackbar
                open={open}
                onClose={() => setOpen(false)}
                autoHideDuration={2000}
                message="Copied to clipboard"
            />

            {tipai && tipai.length > 0 ? (
                sortTemplates(tipai).map((template, i) => {
                    // Skip expired forms
                    if (new Date(template.attributes.validTo).getFullYear() < currentYear) {
                        return null;
                    }
                    
                    // Only show forms with the "published" type
                    if (template.attributes.type !== t('paviesintaNekeisti')) {
                        return null;
                    }
                    
                    const formName = template.attributes.formosPavadinimas;
                    const formType = template.attributes.kasPildo;
                    
                    // Check form status
                    const isFormCreated = isFormaSukurta(formName, formType);
                    const isFormFilled = isFormaUzpildyta(formName, formType);
                    const formId = getFormId(formName, formType);
                    
                    // Is this template currently being created?
                    const isThisFormCreating = isCreating && creatingFormId === template.id;
                    
                    return (
                        <div key={template.id + i} className="row fakeAccordion">
                            <div>
                                <div className="row apklausaName">
                                    <div className="margin row">
                                        <p className="data">{new Date(template.attributes.validTo).getFullYear()}</p>
                                        <p className={template.attributes.type + " statusas"}>
                                            {t('statusas_text')[2]}
                                        </p>
                                    </div>
                                    <p className="margin formosPavadinimas">
                                        {t(template.attributes.formosPavadinimas)}
                                    </p>
                                </div>
                            </div>

                            <div className="row accordionButtons">
                                {isFormCreated ? (
                                    isFormFilled ? (
                                        // Form is created and already filled out
                                        <PrimaryButton
                                            text='apklausa_atlikta'
                                            className='margin'
                                            disabled={true}
                                            size='small'
                                        />
                                    ) : (
                                        // Form is created but not filled out yet
                                        <>
                                            <PrimaryButton
                                                text='kopijuoti_nuoroda'
                                                className='copyBtn margin mobMargin'
                                                startIcon={<Link />}
                                                size='small'
                                                onClick={() => copyFormLink(formId)}
                                            />
                                            <PrimaryButton
                                                text='atlikti_apklausa'
                                                className='margin mobMargin'
                                                size='small'
                                                endIcon={<ArrowForwardIcon />}
                                                onClick={() => openForm(formId)}
                                            />
                                        </>
                                    )
                                ) : (
                                    // Form needs to be created
                                    <PrimaryButton
                                        text={isThisFormCreating ? 'kuriama...' : 'sukurti_apklausa'}
                                        className='margin'
                                        size='small'
                                        disabled={isCreating}
                                        endIcon={isThisFormCreating ? null : <ArrowForwardIcon />}
                                        startIcon={isThisFormCreating ? <CircularProgress size={16} /> : null}
                                        onClick={() => createForm(template.attributes)}
                                    />
                                )}
                            </div>
                        </div>
                    );
                })
            ) : (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                    <p>Nėra aktyvių apklausų šiuo metu arba duomenys dar kraunami.</p>
                    <button 
                        onClick={() => loadAllData()}
                        style={{ 
                            margin: '1rem auto', 
                            padding: '0.5rem 1rem',
                            background: '#0056b3',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        Bandyti dar kartą
                    </button>
                </div>
            )}
        </div>
    );
};

export default FormTable;
